import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

const SURFACE_OPTIONS = [
  { value: 'account_portal', label: 'Account Portal' },
  { value: 'id_portal', label: 'ID Portal' },
];

const MESSAGE_TYPES = ['info', 'success', 'warning', 'critical'];

const EMPTY_MESSAGE_FORM = {
  title: '',
  message_body: '',
  message_type: 'info',
  target_surfaces: ['account_portal', 'id_portal'],
  display_start_at: '',
  display_end_at: '',
  active_flag: true,
};

const SCOPE_LABELS = {
  service_api: 'Service API',
  account_portal: 'Account Portal',
};

const toDatetimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const fromDatetimeLocal = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const formatDateTime = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleString();
};

const formatActor = (name, email, id) => {
  if (name && email) return `${name} (${email})`;
  if (name) return name;
  if (email) return email;
  if (id) return `User #${id}`;
  return 'Unknown';
};

const OpsMaintenance = () => {
  const [activeTab, setActiveTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [messageForm, setMessageForm] = useState(EMPTY_MESSAGE_FORM);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [pendingMaintenance, setPendingMaintenance] = useState(null);
  const [includeArchivedMessages, setIncludeArchivedMessages] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [messageResponse, maintenanceResponse] = await Promise.all([
        api.get('/acct-mgmt/v1/support/platform-messages', { params: { include_archived: includeArchivedMessages } }),
        api.get('/acct-mgmt/v1/support/maintenance-status'),
      ]);
      setMessages(messageResponse.data?.messages || []);
      setStatuses(maintenanceResponse.data?.statuses || []);
      setCacheInfo(maintenanceResponse.data?.cache || null);
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load maintenance controls.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [includeArchivedMessages]);

  const updateMessageForm = (field, value) => {
    setMessageForm((current) => ({ ...current, [field]: value }));
  };

  const toggleSurface = (surface) => {
    setMessageForm((current) => {
      const exists = current.target_surfaces.includes(surface);
      return {
        ...current,
        target_surfaces: exists
          ? current.target_surfaces.filter((item) => item !== surface)
          : [...current.target_surfaces, surface],
      };
    });
  };

  const resetMessageForm = () => {
    setEditingMessageId(null);
    setMessageForm(EMPTY_MESSAGE_FORM);
  };

  const editMessage = (message) => {
    setEditingMessageId(message.id);
    setMessageForm({
      title: message.title || '',
      message_body: message.message_body || '',
      message_type: message.message_type || 'info',
      target_surfaces: message.target_surfaces || [],
      display_start_at: toDatetimeLocal(message.display_start_at),
      display_end_at: toDatetimeLocal(message.display_end_at),
      active_flag: Boolean(message.active_flag),
    });
    setActiveTab('messages');
  };

  const saveMessage = async (event) => {
    event.preventDefault();
    setStatus({ type: '', message: '' });
    const payload = {
      ...messageForm,
      display_start_at: fromDatetimeLocal(messageForm.display_start_at),
      display_end_at: fromDatetimeLocal(messageForm.display_end_at),
    };
    try {
      if (editingMessageId) {
        await api.put(`/acct-mgmt/v1/support/platform-messages/${editingMessageId}`, payload);
      } else {
        await api.post('/acct-mgmt/v1/support/platform-messages', payload);
      }
      resetMessageForm();
      await loadAll();
      setStatus({ type: 'success', message: editingMessageId ? 'Platform message updated.' : 'Platform message created.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to save platform message.' });
    }
  };

  const archiveMessage = async (message) => {
    try {
      await api.post(`/acct-mgmt/v1/support/platform-messages/${message.id}/archive`);
      await loadAll();
      setStatus({ type: 'success', message: 'Platform message archived.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to archive platform message.' });
    }
  };

  const setStatusDraft = (scope, field, value) => {
    setStatuses((current) => current.map((item) => (
      item.scope === scope ? { ...item, [field]: value } : item
    )));
  };

  const submitMaintenance = async (maintenanceStatus) => {
    const payload = {
      active_flag: Boolean(maintenanceStatus.active_flag),
      active_message: maintenanceStatus.active_message || '',
      expected_end_at: fromDatetimeLocal(maintenanceStatus.expected_end_at_draft || toDatetimeLocal(maintenanceStatus.expected_end_at)),
    };
    try {
      await api.put(`/acct-mgmt/v1/support/maintenance-status/${maintenanceStatus.scope}`, payload);
      setPendingMaintenance(null);
      await loadAll();
      window.dispatchEvent(new CustomEvent('finbase:maintenance-status-refresh'));
      setStatus({ type: 'success', message: 'Maintenance status updated.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to update maintenance status.' });
    }
  };

  const requestMaintenanceSave = (maintenanceStatus, nextActive) => {
    const next = { ...maintenanceStatus, active_flag: nextActive };
    setPendingMaintenance(next);
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Maintenance</h2>
          <div className="text-muted">Manage platform messages and active maintenance controls.</div>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadAll} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {status.message && <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button type="button" className={`nav-link ${activeTab === 'messages' ? 'active' : ''}`} onClick={() => setActiveTab('messages')}>
            Platform Messages
          </button>
        </li>
        <li className="nav-item">
          <button type="button" className={`nav-link ${activeTab === 'mode' ? 'active' : ''}`} onClick={() => setActiveTab('mode')}>
            Maintenance Mode
          </button>
        </li>
      </ul>

      {activeTab === 'messages' && (
        <div className="row g-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">{editingMessageId ? 'Edit Platform Message' : 'Create Platform Message'}</h5>
                <form onSubmit={saveMessage}>
                  <div className="row g-2">
                    <div className="col-lg-4">
                      <label className="form-label">Title</label>
                      <input className="form-control" value={messageForm.title} onChange={(e) => updateMessageForm('title', e.target.value)} required />
                    </div>
                    <div className="col-lg-4">
                      <label className="form-label">Type</label>
                      <select className="form-select" value={messageForm.message_type} onChange={(e) => updateMessageForm('message_type', e.target.value)}>
                        {MESSAGE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                      </select>
                    </div>
                    <div className="col-lg-4">
                      <label className="form-label">Active</label>
                      <select className="form-select" value={messageForm.active_flag ? 'true' : 'false'} onChange={(e) => updateMessageForm('active_flag', e.target.value === 'true')}>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="form-label">Message</label>
                    <textarea className="form-control" rows="3" value={messageForm.message_body} onChange={(e) => updateMessageForm('message_body', e.target.value)} required />
                  </div>
                  <div className="row g-2 mt-1">
                    <div className="col-md-6">
                      <label className="form-label">Display Start (your local time)</label>
                      <input type="datetime-local" className="form-control" value={messageForm.display_start_at} onChange={(e) => updateMessageForm('display_start_at', e.target.value)} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Display End (your local time)</label>
                      <input type="datetime-local" className="form-control" value={messageForm.display_end_at} onChange={(e) => updateMessageForm('display_end_at', e.target.value)} required />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="form-label d-block">Target Surfaces</label>
                    {SURFACE_OPTIONS.map((option) => (
                      <div className="form-check form-check-inline" key={option.value}>
                        <input className="form-check-input" type="checkbox" checked={messageForm.target_surfaces.includes(option.value)} onChange={() => toggleSurface(option.value)} id={`surface-${option.value}`} />
                        <label className="form-check-label" htmlFor={`surface-${option.value}`}>{option.label}</label>
                      </div>
                    ))}
                  </div>
                  <div className="d-flex gap-2 mt-3">
                    <button type="submit" className="btn btn-primary">{editingMessageId ? 'Save Message' : 'Create Message'}</button>
                    {editingMessageId && <button type="button" className="btn btn-outline-secondary" onClick={resetMessageForm}>Cancel</button>}
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <h5 className="card-title mb-0">Platform Messages</h5>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="view-archived-platform-messages"
                      checked={includeArchivedMessages}
                      onChange={(e) => setIncludeArchivedMessages(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="view-archived-platform-messages">
                      View Archived
                    </label>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Type</th>
                        <th>Window</th>
                        <th>Created</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.map((message) => (
                        <tr key={message.id}>
                          <td>
                            <div className="fw-semibold">{message.title}</div>
                            <div className="text-muted small">{(message.target_surfaces || []).join(', ')}</div>
                          </td>
                          <td>{message.message_type}</td>
                          <td className="small">{formatDateTime(message.display_start_at)}<br />{formatDateTime(message.display_end_at)}</td>
                          <td className="small">
                            <div>{formatActor(message.created_by_name, message.created_by_email, message.created_by)}</div>
                            <div className="text-muted">{formatDateTime(message.created_at)}</div>
                          </td>
                          <td>{message.archived_at ? 'Archived' : (message.active_flag ? 'Active' : 'Inactive')}</td>
                          <td className="text-end">
                            <button type="button" className="btn btn-outline-primary btn-sm me-2" onClick={() => setSelectedMessage(message)}>View</button>
                            {!message.archived_at && (
                              <>
                                <button type="button" className="btn btn-outline-secondary btn-sm me-2" onClick={() => editMessage(message)}>Edit</button>
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => archiveMessage(message)}>Archive</button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                      {messages.length === 0 && (
                        <tr><td colSpan="6" className="text-muted text-center">No platform messages.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'mode' && (
        <div className="row g-3">
          {statuses.map((item) => (
            <div className="col-lg-6" key={item.scope}>
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between gap-2 mb-2">
                    <h5 className="card-title mb-0">{SCOPE_LABELS[item.scope] || item.scope}</h5>
                    <span className={`badge ${item.active_flag ? 'bg-danger' : 'bg-success'}`}>{item.active_flag ? 'Maintenance On' : 'Active'}</span>
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Active Maintenance Message</label>
                    <textarea className="form-control" rows="4" value={item.active_message || ''} onChange={(e) => setStatusDraft(item.scope, 'active_message', e.target.value)} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Expected End (your local time)</label>
                    <input type="datetime-local" className="form-control" value={item.expected_end_at_draft ?? toDatetimeLocal(item.expected_end_at)} onChange={(e) => setStatusDraft(item.scope, 'expected_end_at_draft', e.target.value)} />
                  </div>
                  <div className="text-muted small mb-3">Last updated: {formatDateTime(item.updated_at)}</div>
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => submitMaintenance(item)}>Save Message</button>
                    {item.active_flag ? (
                      <button type="button" className="btn btn-success" onClick={() => requestMaintenanceSave(item, false)}>Disable Maintenance</button>
                    ) : (
                      <button type="button" className="btn btn-danger" onClick={() => requestMaintenanceSave(item, true)}>Enable Maintenance</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="col-12">
            <div className="text-muted small">
              Cache refresh interval: {cacheInfo?.refresh_ms ? `${Math.round(cacheInfo.refresh_ms / 1000)} seconds` : 'Unknown'}.
              Last cache load: {formatDateTime(cacheInfo?.loaded_at)}.
            </div>
          </div>
        </div>
      )}

      {selectedMessage && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">{selectedMessage.title}</h5>
                  <div className="text-muted small">
                    {selectedMessage.message_type} message for {(selectedMessage.target_surfaces || []).join(', ')}
                  </div>
                </div>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelectedMessage(null)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <div className="text-muted small text-uppercase">Message</div>
                  <div className="border rounded p-3 bg-light" style={{ whiteSpace: 'pre-wrap' }}>{selectedMessage.message_body}</div>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="text-muted small text-uppercase">Display Window</div>
                    <div>{formatDateTime(selectedMessage.display_start_at)}</div>
                    <div>{formatDateTime(selectedMessage.display_end_at)}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted small text-uppercase">Status</div>
                    <div>{selectedMessage.archived_at ? 'Archived' : (selectedMessage.active_flag ? 'Active' : 'Inactive')}</div>
                    {selectedMessage.archived_at && <div className="text-muted small">Archived: {formatDateTime(selectedMessage.archived_at)}</div>}
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted small text-uppercase">Created</div>
                    <div>{formatActor(selectedMessage.created_by_name, selectedMessage.created_by_email, selectedMessage.created_by)}</div>
                    <div className="text-muted small">{formatDateTime(selectedMessage.created_at)}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted small text-uppercase">Last Updated</div>
                    <div>{formatActor(selectedMessage.updated_by_name, selectedMessage.updated_by_email, selectedMessage.updated_by)}</div>
                    <div className="text-muted small">{formatDateTime(selectedMessage.updated_at)}</div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                {!selectedMessage.archived_at && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      editMessage(selectedMessage);
                      setSelectedMessage(null);
                    }}
                  >
                    Edit
                  </button>
                )}
                <button type="button" className="btn btn-primary" onClick={() => setSelectedMessage(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pendingMaintenance && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{pendingMaintenance.active_flag ? 'Enable maintenance mode?' : 'Disable maintenance mode?'}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setPendingMaintenance(null)} />
              </div>
              <div className="modal-body">
                {pendingMaintenance.active_flag ? (
                  <p>
                    This will block customer-facing access for {SCOPE_LABELS[pendingMaintenance.scope] || pendingMaintenance.scope}.
                    Ops access will remain available.
                  </p>
                ) : (
                  <p>
                    This will restore customer-facing access for {SCOPE_LABELS[pendingMaintenance.scope] || pendingMaintenance.scope}.
                  </p>
                )}
                <p className="mb-0 text-muted small">
                  In multi-instance environments, allow up to one configured cache refresh interval for every app instance to pick up the change.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setPendingMaintenance(null)}>Cancel</button>
                <button type="button" className={pendingMaintenance.active_flag ? 'btn btn-danger' : 'btn btn-success'} onClick={() => submitMaintenance(pendingMaintenance)}>
                  {pendingMaintenance.active_flag ? 'Enable Maintenance' : 'Disable Maintenance'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpsMaintenance;
